import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import styles from './CertificationDashboard.module.css';
import DataFallback from '../components/feedback/DataFallback';

function CertificationDashboard() {
  const navigate = useNavigate();
  const { user_type } = useSelector(state => state.user);
  const [certificationStatus, setCertificationStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [showSurvey, setShowSurvey] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    fetchCertificationStatus();
  }, []);

  const fetchCertificationStatus = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setError('Authentication required');
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/certification/status/`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        { const e = new Error(`HTTP ${response.status}`); e.status = response.status; throw e; }
      }

      const data = await response.json();
      setCertificationStatus(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchQuestions = async () => {
    const token = localStorage.getItem('token');
    
    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/certification/questions/`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        { const e = new Error(`HTTP ${response.status}`); e.status = response.status; throw e; }
      }

      const data = await response.json();
      setQuestions(data.questions || []);
      setShowSurvey(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAnswerChange = (questionId, optionId) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: optionId
    }));
  };

  const submitSurvey = async () => {
    const token = localStorage.getItem('token');
    const answersArray = Object.entries(answers).map(([questionId, optionId]) => ({
      question: parseInt(questionId),
      selected_option: optionId
    }));

    try {
      setSubmitting(true);
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/certification/submit/`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ answers: answersArray }),
      });

      if (!response.ok) {
        throw new Error('Failed to submit survey');
      }

      const result = await response.json();
      
      // Show success message and update status
      setSuccessMessage(`Survey submitted successfully! Your grade: ${result.grade} (${result.percentage}%)`);
      setShowSurvey(false);
      fetchCertificationStatus(); // Refresh status
      
      // Hide success message after 5 seconds
      setTimeout(() => {
        setSuccessMessage('');
      }, 5000);
      
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const viewCertificate = (certificate) => {
    // Navigate to certificate view page with certificate data
    navigate(`/certificate-view/${certificate.id}`, { 
      state: { certificate } 
    });
  };

  const downloadCertificatePDF = async (certificate) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/certification/certificate/${certificate.id}/pdf/`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to download certificate');
      }

      // Create blob and download
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = `3SC_Certificate_${certificate.grade}_${certificate.id}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('Download failed:', err);
      // Fallback: open certificate view for manual print
      viewCertificate(certificate);
    }
  };

  const renderCertificationStatus = () => {
    if (loading) return <div className={styles.loading}>Loading certification status...</div>;
    if (error) return (
      <DataFallback
        variant="pane"
        error={typeof error === 'string' ? { message: error, retryable: true } : error}
        onRetry={() => window.location.reload()}
      />
    );
    if (!certificationStatus) return <div className={styles.error}>No certification data found</div>;

    const { status } = certificationStatus;

    if (status === 'not_taken') {
      return (
        <div className={styles.statusCard + ' ' + styles.notTaken}>
          <h3>3SC Certification</h3>
          <p>Take your annual sustainability certification survey</p>
          <button onClick={fetchQuestions} className={styles.primaryBtn}>
            Take Survey
          </button>
        </div>
      );
    }

    if (status === 'completed') {
      const cert = certificationStatus.certificate;
      return (
        <div className={styles.statusCard + ' ' + styles.completed}>
          <h3>3SC Certification</h3>
          <div className={`${styles.gradeBadge} ${styles['grade' + cert.grade.replace(/[+-]/g, '')]}`}>
            {cert.grade}
          </div>
          <p>Score: {cert.percentage_score}% ({cert.total_score}/{cert.max_possible_score} points)</p>
          <p>Valid until: {new Date(cert.expires_at).toLocaleDateString()}</p>
          <div className={styles.buttonGroup}>
            <button onClick={() => viewCertificate(cert)} className={styles.secondaryBtn}>
              View Certificate
            </button>
            <button onClick={() => downloadCertificatePDF(cert)} className={styles.outlineBtn}>
              Download PDF
            </button>
          </div>
        </div>
      );
    }

    if (status === 'expired') {
      const lastCert = certificationStatus.last_certification;
      return (
        <div className={styles.statusCard + ' ' + styles.expired}>
          <h3>3SC Certification</h3>
          <div className={`${styles.gradeBadge} ${styles.expired}`}>
            {lastCert.grade} (Expired)
          </div>
          <p>Last score: {lastCert.percentage}% • Expired: {new Date(lastCert.expired_at).toLocaleDateString()}</p>
          <button onClick={fetchQuestions} className={styles.primaryBtn}>
            Renew Certification
          </button>
        </div>
      );
    }

    return <div className={styles.error}>Unknown certification status</div>;
  };

  const renderSurvey = () => {
    if (!showSurvey) return null;

    return (
      <div className={styles.surveyContainer}>
        <div className={styles.surveyHeader}>
          <h2>3SC Certification Survey</h2>
          <p>Please answer all questions to receive your sustainability certification.</p>
          <button 
            onClick={() => setShowSurvey(false)} 
            className={styles.closeBtn}
          >
            ×
          </button>
        </div>
        
        <div className={styles.questionsContainer}>
          {questions.map((question) => (
            <div key={question.id} className={styles.questionCard}>
              <h4>{question.text}</h4>
              {question.requires_document && (
                <p className={styles.documentNote}>
                  📎 This question may require document upload
                </p>
              )}
              
              <div className={styles.optionsContainer}>
                {question.options.map((option) => (
                  <label key={option.id} className={styles.optionLabel}>
                    <input
                      type="radio"
                      name={`question_${question.id}`}
                      value={option.id}
                      onChange={() => handleAnswerChange(question.id, option.id)}
                      checked={answers[question.id] === option.id}
                    />
                    <span>{option.text}</span>
                  </label>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className={styles.surveyFooter}>
          <button 
            onClick={submitSurvey} 
            disabled={submitting || Object.keys(answers).length !== questions.length}
            className={styles.submitBtn}
          >
            {submitting ? 'Submitting...' : 'Submit Survey'}
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className={styles.dashboard}>
      {successMessage && (
        <div className={styles.successMessage}>
          <div className={styles.successContent}>
            <span className={styles.successIcon}>✅</span>
            {successMessage}
            <button 
              onClick={() => setSuccessMessage('')}
              className={styles.closeSuccess}
            >
              ×
            </button>
          </div>
        </div>
      )}
      
      <div className={styles.header}>
        <h1>3SC Certification Dashboard</h1>
        <p>Suspire Sustainability Certification System</p>
      </div>

      {user_type === 'appuser' && (
        <div className={styles.brandSection}>
          {renderCertificationStatus()}
          {renderSurvey()}
        </div>
      )}

      {user_type === 'employee' && (
        <div className={styles.employeeSection}>
          <div className={styles.infoCard}>
            <h3>Employee Access</h3>
            <p>
              As an employee, you can view certification status for all brands in the 
              <a href="/brands" className={styles.link}> Brands Dashboard</a>.
            </p>
            <p>
              Brand certification data is automatically displayed in the brands list 
              with status badges and grade information.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

export default CertificationDashboard;
